import { useState, type FormEvent } from "react";
import { motion } from "motion/react";
import { ApiClientError } from "@victor/api-client";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Logo } from "../../ui/Logo";

type Step =
  | { kind: "credentials" }
  | { kind: "mfa"; mfaToken: string };

export function LoginPage() {
  const { login, loginMfa } = useAuth();
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await login({ email, password });
      if ("mfaRequired" in result) {
        setStep({ kind: "mfa", mfaToken: result.mfaToken });
        setCode("");
      }
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : "Erreur de connexion.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    if (step.kind !== "mfa") return;
    setError(null);
    setBusy(true);
    try {
      await loginMfa({ mfaToken: step.mfaToken, code });
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : "Code MFA invalide.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-50/40 px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="rounded-2xl bg-white p-8 shadow-xl shadow-navy-800/5 ring-1 ring-navy-100">
          <div className="flex items-center justify-center">
            <Logo size={48} withWordmark />
          </div>
          <p className="mt-2 text-center text-xs font-bold uppercase tracking-wider text-accent-500">
            Console MyVictor
          </p>

          {step.kind === "credentials" ? (
            <form onSubmit={submitCredentials} className="mt-6 space-y-4">
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                label="Mot de passe"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button type="submit" fullWidth disabled={busy}>
                {busy ? "Connexion…" : "Se connecter"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitMfa} className="mt-6 space-y-4">
              <p className="rounded-xl bg-accent-50 px-3 py-2 text-sm text-accent-700 ring-1 ring-accent-100">
                Code à 6 chiffres depuis votre application TOTP, ou un de
                vos 10 codes de récupération.
              </p>
              <Input
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                autoComplete="one-time-code"
                placeholder="123 456"
                required
              />
              <Button type="submit" fullWidth disabled={busy}>
                {busy ? "Vérification…" : "Valider"}
              </Button>
              <button
                type="button"
                onClick={() => setStep({ kind: "credentials" })}
                className="block w-full text-center text-xs font-semibold text-navy-500 hover:text-navy-800"
              >
                Recommencer la connexion
              </button>
            </form>
          )}

          {error ? (
            <p
              role="alert"
              className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200"
            >
              {error}
            </p>
          ) : null}
        </div>

        <p className="mt-6 text-center text-xs text-navy-400">
          Accès réservé à l'équipe MyVictor. Toute tentative d'accès est
          journalisée.
        </p>
      </motion.div>
    </div>
  );
}
